import { Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Subscription } from 'rxjs';
import { DwPubSubDebugEvent, DwPubSubDebugSnapshot } from '../../models/dw-pub-sub-debug-event.model';
import { DwPubSubService } from '../../dw-pub-sub-service';
import { DwSecurityUserService } from '@devwareapps/devware-cap';
import { AppPermissions } from '../../../../../meta-data/app-permissions.enum';

@Component({
  selector: 'dw-pubsub-monitor',
  templateUrl: './dw-pubsub-monitor.component.html',
  styleUrls: ['./dw-pubsub-monitor.component.scss']
})
export class DwPubSubMonitorComponent implements OnInit, OnDestroy {
  @Input() title = 'Real-Time Monitor';
  @Input() collapsedByDefault = true;
 
  @ViewChild('logViewport', { static: false }) logViewport?: ElementRef<HTMLDivElement>;

  hideSessionInfo = false;
  isOpen = !this.collapsedByDefault;

  autoScroll = true;
  paused = false;

  snapshot?: DwPubSubDebugSnapshot;

  filterText = '';
  level: 'All' | 'State' | 'Send' | 'Received' | 'Info' | 'Warn' | 'Error' = 'All';

  events: DwPubSubDebugEvent[] = [];
  filtered: DwPubSubDebugEvent[] = [];

  private sub = new Subscription();
  subscriptionsSetup: boolean;

  constructor(private pubsub: DwPubSubService, private dwSecurityUserService: DwSecurityUserService) {}

  canViewMonitor: boolean = false;

  ngOnInit(): void {
    
    this.dwSecurityUserService.securityContextChanged().subscribe(() => {
      this.canViewMonitor = this.hasPermission();
      
      this.setupSubscriptions();
    })
    
    this.canViewMonitor = this.hasPermission();
    this.setupSubscriptions();
  }

  private hasPermission() {
    const authUser = this.dwSecurityUserService.securityContext?.AuthenticatedUser;

    if (!authUser) {
      return false;
    }

    if (this.dwSecurityUserService.hasPermissionUser(authUser, AppPermissions.lMSAdminMenuAccess)) {
      return true;
    }

    return false;

  }

  setupSubscriptions(): void {
    if (!this.canViewMonitor) {
      return;
    }

    if (this.subscriptionsSetup) {
      return;
    }

    this.subscriptionsSetup = true;

    // Seed buffered events if your service supports it
    const buffer = (this.pubsub as any).getDebugBuffer?.() as DwPubSubDebugEvent[] | undefined;
    this.events = buffer ? buffer : [];
    this.applyFilters(true);

    const snap$ = (this.pubsub as any).debugSnapshot$;
    if (snap$) {
      this.sub.add(snap$.subscribe((s: DwPubSubDebugSnapshot) => (this.snapshot = s)));
    }

    const events$ = (this.pubsub as any).debugEvents$;
    if (events$) {
      this.sub.add(
        events$.subscribe((e: DwPubSubDebugEvent) => {
          if (this.paused) return;

          this.events.push(e);
          if (this.events.length > 500) this.events.shift();

          // Default: JSON hidden
          if ((e as any).__expanded == null) (e as any).__expanded = false;

          this.applyFilters();
          if (this.autoScroll) this.scrollToBottomSoon();
        })
      );
    }
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }

  toggleOpen(): void {
    this.isOpen = !this.isOpen;
    if (this.isOpen && this.autoScroll) this.scrollToBottomSoon();
  }

  clear(): void {
    (this.pubsub as any).clearDebugBuffer?.();
    this.events = [];
    this.filtered = [];
  }

  toggleExpand(e: DwPubSubDebugEvent): void {
    (e as any).__expanded = !(e as any).__expanded;
    if ((e as any).__expanded && this.autoScroll) this.scrollToBottomSoon();
  }

  collapseAll(): void {
    this.filtered.forEach(e => ((e as any).__expanded = false));
  }

  copySnapshot(): void {
    const payload = {
      snapshot: this.snapshot,
      events: this.filtered.slice(-200)
    };
    copyToClipboard(JSON.stringify(payload, null, 2));
  }

  copyEvent(e: DwPubSubDebugEvent): void {
    copyToClipboard(JSON.stringify(e, null, 2));
  }

  applyFilters(initial = false): void {
    const text = (this.filterText || '').toLowerCase().trim();

    this.filtered = this.events.filter(e => {
      if (this.level !== 'All' && e.Kind !== this.level) return false;
      if (!text) return true;

      const hay = `${e.Kind} ${e.Label} ${tryStringify(e.Data)}`.toLowerCase();
      return hay.includes(text);
    });

    if (!initial && this.autoScroll) this.scrollToBottomSoon();
  }

  badgeClass(kind: DwPubSubDebugEvent['Kind']): string {
    switch (kind) {
      case 'State': return 'badge badge-state';
      case 'Received': return 'badge badge-received';
      case 'Send': return 'badge badge-send';
      case 'Info': return 'badge badge-info';
      case 'Warn': return 'badge badge-warn';
      case 'Error': return 'badge badge-error';
      default: return 'badge';
    }
  }

  statePillClass(state?: string): string {
    switch (state) {
      case 'Open': return 'pill pill-ok';
      case 'Connecting':
      case 'Reconnecting': return 'pill pill-warn';
      case 'Closed':
      case 'Error': return 'pill pill-bad';
      default: return 'pill';
    }
  }

  // mm/dd hh:mm TT -> Angular: MM/dd hh:mm a
  // (Template uses date pipe; this just keeps it obvious)
  readonly dtFmt = 'hh:mm:ss';

  private scrollToBottomSoon(): void {
    setTimeout(() => {
      const el = this.logViewport?.nativeElement;
      if (!el) return;
      el.scrollTop = el.scrollHeight;
    }, 0);
  }
}

function tryStringify(v: any): string {
  try {
    if (v == null) return '';
    if (typeof v === 'string') return v;
    return JSON.stringify(v);
  } catch {
    return String(v);
  }
}

function copyToClipboard(text: string): void {
  const navAny: any = navigator as any;
  if (navAny.clipboard?.writeText) {
    navAny.clipboard.writeText(text);
    return;
  }

  const ta = document.createElement('textarea');
  ta.value = text;
  ta.style.position = 'fixed';
  ta.style.opacity = '0';
  document.body.appendChild(ta);
  ta.focus();
  ta.select();
  document.execCommand('copy');
  document.body.removeChild(ta);
}
